import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {EnvironmentSchema, ENVIRONMENTS_MAX_AGE_MS} from '@github-ui/copilot-shared-ui/environments/use-environments'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import {environmentApiPath} from '../utils/api-url-helpers'
import {agentEnvironmentQueryKey} from './use-environment'
const EnvironmentValidator = Compile(EnvironmentSchema)

interface UseEnvironmentFromApiOptions {
  enabled?: boolean
  subscribed?: boolean
  refetchInterval?: number | false
  refetchOnWindowFocus?: boolean
}

export function useEnvironmentFromApi(
  environmentId: string | undefined,
  apiURL: string | undefined,
  {
    enabled = true,
    subscribed = true,
    refetchInterval = false,
    refetchOnWindowFocus = false,
  }: UseEnvironmentFromApiOptions = {},
) {
  const queryEnabled = enabled && !!environmentId
  const {sendAnalyticsEvent} = useAnalytics()
  const persister = useMemo(
    () =>
      createPersister({
        validator: EnvironmentValidator,
        sendAnalyticsEvent,
        prefix: 'copilot-agent-environment',
        maxAgeMs: ENVIRONMENTS_MAX_AGE_MS,
        enabled: queryEnabled,
      }),
    [sendAnalyticsEvent, queryEnabled],
  )

  return useQuery<Environment | null>({
    queryKey: agentEnvironmentQueryKey(environmentId, apiURL),
    enabled: queryEnabled,
    subscribed,
    refetchInterval,
    refetchOnWindowFocus,
    queryFn: async ({signal}) => {
      if (!environmentId) return null
      const response = await fetchCAPI({
        path: environmentApiPath(environmentId),
        signal,
        ...(apiURL ? {copilotApiUrl: apiURL} : {}),
      })
      const data = await response.json()
      if (!data || typeof data !== 'object' || Array.isArray(data)) return null
      const {webpubsub: _webpubsub, ...environment} = data as Environment
      return environment
    },
    persister,
  })
}
